import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Cognito, EnvService, JwtService, LoggerService, SessionToken, User, UserOrg } from '@enliven/angular';
import { Observable, catchError, map, of, switchMap, throwError } from 'rxjs';
import {
  ACTIVE_USER,
  COGNITO,
  NEXT_ORG_E360_URL,
  SIGN_IN_URL,
  SSO_SIGN_OUT_MSG,
  SSO_USER_SIGNED_IN
} from '../constants/storage-constants';
import { SignOutOpts } from '../models/sign-out-opts.model';
import { DataService } from './data.service';
import { E360SessionService } from './e360-session.service';
import { StateService } from './state.service';
import { UrlParamService } from './url-param.service';

@Injectable({
  providedIn: 'root'
})
export class CognitoService {
  private readonly http: HttpClient = inject(HttpClient);
  private readonly dataService: DataService = inject(DataService);
  private readonly e360SessionService: E360SessionService = inject(E360SessionService);
  private readonly envService: EnvService = inject(EnvService);
  private readonly stateService: StateService = inject(StateService);
  private readonly logger: LoggerService = inject(LoggerService);
  private readonly urlParamService: UrlParamService = inject(UrlParamService);
  private readonly jwtService: JwtService = inject(JwtService);

  baseUrl: string;
  cognitoClientId: string;
  userPoolUri: string;

  private readonly localBaseUrl: string = 'https://localhost:4200';

  // This represents a chain of events that occurs when a user logs in via SSO
  // 1. call to the oauth cognito URL to retrieve tokens from the grant code
  // 2. set session tokens and some storage variables
  // 3. call to get the org info from the org returned from the decoded id token
  // 4. set the session storage org ID
  // 5. upsert a user for the org, which returns an EnlivenUser. Currently we do nothing with the enliven user
  // 6a. return true if everything succeeds during sign in
  // 6b. return false if any of the above errors
  authenticateViaGrantCode(code: string): Observable<{ isAuthenticated: boolean; error?: string }> {
    return this.getTokensFromCode(code).pipe(
      switchMap((tokenResponse: SessionToken) => {
        this.e360SessionService.setSessionTokens(tokenResponse);
        this.e360SessionService.cleanupStorageAfterSignIn(true); // SSO = true
        const providerName: any = this.jwtService.decodeToken(tokenResponse.idToken).identities[0].providerName;
        return this.dataService.getOrgById(providerName, tokenResponse.idToken);
      }),
      switchMap(({ organizationId }: UserOrg) => this.dataService.upsertSsoUser(organizationId)),
      map((enlivenUser: User) => ({ isAuthenticated: true })),
      catchError(err => {
        return of({ isAuthenticated: false, error: err?.error?.errors[0]?.message });
      })
    );
  }

  authorize(cognito: Cognito): void {
    const params = new HttpParams()
      .append('client_id', cognito.clientId)
      .append('redirect_uri', this.envService.isLocal ? this.localBaseUrl : cognito.websiteBaseUrl)
      .append('response_type', 'code')
      .append('identity_provider', cognito.clientName);
    window.open(`${cognito.userPoolUri}/oauth2/authorize?${params.toString()}`, '_self');
  }

  signOut({ message, nextE360Url, displaySignInLink }: SignOutOpts) {
    sessionStorage.clear();
    localStorage.removeItem(COGNITO);
    localStorage.removeItem(SSO_USER_SIGNED_IN);
    localStorage.setItem(SSO_SIGN_OUT_MSG, message);
    if (displaySignInLink) {
      this.buildReSignInLink();
    }
    if (nextE360Url) {
      localStorage.setItem(NEXT_ORG_E360_URL, nextE360Url);
    }
    this.e360SessionService.clearAccessTokenExpiryTimer();
    this.e360SessionService.clearSessionDetails();
    this.stateService.clear();
    const errorCodes: string[] = [
      'ALERT_PAGE.SSO_SIGN_IN_FAILED',
      'ALERT_PAGE.ACCESS_DENIED',
      'ALERT_PAGE.PROBLEM_GATHERING_DATA',
      'ALERT_PAGE.SSO_USER_DEACTIVATED'
    ];
    const logoutUriPath: string = errorCodes.includes(message) ? 'error' : 'signOut';
    const params = new HttpParams()
      .append('client_id', this.cognitoClientId)
      .append('logout_uri', `${this.baseUrl}/${logoutUriPath}`);
    window.open(`${this.userPoolUri}/logout?${params.toString()}`, '_self');
  }

  // TODO error handling
  // https://docs.aws.amazon.com/cognito/latest/developerguide/token-endpoint.html
  refreshTokens(refreshToken: string): Observable<SessionToken> {
    return this.e360SessionService.refreshTokensViaHttp(refreshToken, this.userPoolUri, this.cognitoClientId);
  }

  setUserPoolInfo(cognito: Cognito) {
    this.baseUrl = this.envService.isLocal ? this.localBaseUrl : cognito.websiteBaseUrl;
    this.cognitoClientId = cognito.clientId;
    this.userPoolUri = cognito.userPoolUri;
  }

  buildReSignInLink(): void {
    const signInAgainUrl: string = `${this.baseUrl}/?organization=${this.urlParamService.orgIdpExternalId}`;
    localStorage.setItem(SIGN_IN_URL, signInAgainUrl);
  }

  // https://docs.aws.amazon.com/cognito/latest/developerguide/token-endpoint.html
  private getTokensFromCode(code: string): Observable<SessionToken> {
    const headers = new HttpHeaders().set('content-type', 'application/x-www-form-urlencoded');
    const params = new URLSearchParams();
    params.set('code', code);
    params.set('client_id', this.cognitoClientId);
    params.set('redirect_uri', this.baseUrl);
    params.set('grant_type', 'authorization_code');
    const endpoint: string = this.userPoolUri + '/oauth2/token';

    return this.http.post<string>(endpoint, params, { headers }).pipe(
      map((response: any) => {
        return {
          idToken: response.id_token,
          accessToken: response.access_token,
          refreshToken: response.refresh_token
        };
      }),
      catchError((err: HttpErrorResponse) => {
        this.logger.error(
          'Error during POST of tokens from authorization_code to User API',
          {
            endpoint,
            params: params.toString()
          },
          err
        );
        return throwError(() => err);
      })
    );
  }
}
